// client/src/pages/POA/ClientSearchModal.tsx
// Step 0 of the POA flow: search for and confirm the PE client before the form begins.
// Debounced, 3-char minimum, calls /api/pe-client-search?q=...

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Loader, Building2, CheckCircle2 } from 'lucide-react';

const KB_LOGO = 'https://storageacctbmssprod001.blob.core.windows.net/container-bmssprod001-public/images/KB-Favicon-New.png';

export interface PEClient {
  clientCode:        string;
  clientName:        string;
  contIndex:         number | null;
  partnerName:       string;
  managerName:       string;
  officeTitle:       string;
}

interface Props {
  onSelect:   (client: PEClient) => void;
  onAiAssist?: () => void;  // callback to open AI assist modal
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px 10px 38px', borderRadius: 10,
  border: '1px solid var(--border)',
  background: 'rgba(255,255,255,.05)', color: 'var(--text)',
  fontSize: 15, outline: 'none',
};

async function fetchClients(q: string): Promise<PEClient[]> {
  const res = await fetch(`/api/pe-client-search?q=${encodeURIComponent(q)}`);
  if (!res.ok) throw new Error(`Search failed (${res.status})`);
  const data = await res.json();
  return data.clients ?? [];
}

export default function ClientSearchModal({ onSelect, onAiAssist }: Props) {
  const [query,    setQuery]    = useState('');
  const [results,  setResults]  = useState<PEClient[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef    = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const runSearch = useCallback(async (q: string) => {
    if (q.length < 3) {
      setResults([]);
      setSearched(false);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const clients = await fetchClients(q);
      setResults(clients);
      setSearched(true);
    } catch (e: any) {
      setError(e.message || 'Search failed. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(val.trim()), 300);
  }, [runSearch]);

  return (
    /* Full-page modal overlay */
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div className="card" style={{ width: '100%', maxWidth: 580, maxHeight: '80vh', display: 'flex', flexDirection: 'column', background: 'var(--surface, #1e2030)', border: '1px solid rgba(122,162,255,.25)', boxShadow: '0 8px 40px rgba(0,0,0,.6)' }}>
        {/* Header */}
        <div className="hd" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>New Power of Attorney</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 3 }}>
              Search for the client this Form 2848 covers
            </div>
          </div>
          {onAiAssist && (
            <button
              onClick={onAiAssist}
              title="Use AI to prepare this POA"
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '7px 14px', borderRadius: 99, border: '1px solid rgba(122,162,255,.3)',
                background: 'rgba(122,162,255,.1)', cursor: 'pointer',
                color: '#7aa2f7', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                transition: 'background .15s, border-color .15s',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(122,162,255,.2)'; e.currentTarget.style.borderColor = 'rgba(122,162,255,.5)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(122,162,255,.1)'; e.currentTarget.style.borderColor = 'rgba(122,162,255,.3)'; }}
            >
              <img src={KB_LOGO} alt="" width={22} height={22} style={{ borderRadius: 99 }} />
              Use AI Assist
            </button>
          )}
        </div>

        {/* Search input */}
        <div className="bd" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 14 }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={16}
              style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }}
            />
            {loading && (
              <Loader
                size={15}
                style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', animation: 'spin 1s linear infinite' }}
              />
            )}
            <input
              ref={inputRef}
              style={inputStyle}
              value={query}
              onChange={handleChange}
              placeholder="Type client name or code (e.g. Myers or 01085)"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          {query.length > 0 && query.length < 3 && (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
              Keep typing — 3 characters minimum to search
            </div>
          )}
        </div>

        {/* Results */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {error && (
            <div style={{ padding: '12px 14px', color: 'var(--bad)', fontSize: 13 }}>{error}</div>
          )}

          {!error && searched && results.length === 0 && (
            <div style={{ padding: '24px 14px', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
              No clients found matching "{query}"
            </div>
          )}

          {!error && !searched && !loading && (
            <div style={{ padding: '24px 14px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
              <Building2 size={32} style={{ marginBottom: 10, opacity: .4 }} />
              <div>Start typing to search 8,000+ clients</div>
              <div style={{ fontSize: 11, marginTop: 4, opacity: .7 }}>First search may take a few seconds while the directory loads</div>
            </div>
          )}

          {results.map((c) => (
            <button
              key={c.clientCode}
              onClick={() => onSelect(c)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '11px 14px', border: 'none', borderBottom: '1px solid var(--border)',
                background: 'transparent', cursor: 'pointer', color: 'var(--text)',
                transition: 'background .12s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(122,162,255,.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{c.clientName}</span>
                  <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 8 }}>{c.clientCode}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                  <CheckCircle2 size={14} style={{ color: 'var(--muted)', opacity: .4 }} />
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3, display: 'flex', gap: 16 }}>
                {c.partnerName && <span>Partner: {c.partnerName}</span>}
                {c.officeTitle && <span>Office: {c.officeTitle}</span>}
              </div>
            </button>
          ))}

          {results.length === 250 && (
            <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>
              Showing top 250 matches — refine your search to narrow results
            </div>
          )}
        </div>
      </div>

      {/* Spinner keyframe — injected once */}
      <style>{`@keyframes spin { from { transform: translateY(-50%) rotate(0deg); } to { transform: translateY(-50%) rotate(360deg); } }`}</style>
    </div>
  );
}
